import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import useSEO from '@/hooks/useSEO';
import { AppleWalletPassVisual } from '@/pages/business-card/components/AppleWalletCard';
import DigitalCardExport from '@/pages/business-card/components/DigitalCardExport';

// ─── Card Registry ────────────────────────────────────────────────────────────

interface ContactCard {
  slug: string;
  name: string;
  firstName: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  tagline: string;
  image: string;
  linkedin?: string;
}

const CARD_REGISTRY: ContactCard[] = [
  {
    slug: 'hector',
    name: 'Hector',
    firstName: 'Hector',
    title: 'Principal & Managing Director',
    company: 'Higher Standard Global',
    email: 'Hector.S@higherstandardglobal.com',
    phone: '+1 (307) 218 3610',
    website: 'higherstandardglobal.com',
    tagline: 'By Reference Only',
    image: 'https://public.readdy.ai/ai/img_res/4f0c0a92a0dc59cfda664d5d95eca135.jpg',
  },
];

// ─── Image → Base64 ──────────────────────────────────────────────────────────

async function imageUrlToBase64(url: string): Promise<{ b64: string; mime: string } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const mime = blob.type || 'image/jpeg';
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const b64 = result.split(',')[1] ?? '';
        resolve({ b64, mime });
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── vCard Builder ────────────────────────────────────────────────────────────

function buildVCard(c: ContactCard, photoB64?: string, photoMime?: string): string {
  const lines: string[] = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${c.name};;;; `,
    `FN:${c.name}`,
    `TITLE:${c.title}`,
    `ORG:${c.company}`,
    `EMAIL;TYPE=WORK,INTERNET:${c.email}`,
    `TEL;TYPE=WORK,VOICE:${c.phone}`,
    `URL:https://${c.website.replace(/^https?:\/\//, '')}`,
    `NOTE:${c.tagline} — ${c.company}`,
  ];

  if (photoB64 && photoMime) {
    const typeStr = photoMime.split('/')[1]?.toUpperCase() ?? 'JPEG';
    const header = `PHOTO;ENCODING=b;TYPE=${typeStr}:`;
    const chunkSize = 75 - header.length;
    lines.push(header + photoB64.slice(0, chunkSize));
    const rest = photoB64.slice(chunkSize);
    for (let i = 0; i < rest.length; i += 74) {
      lines.push(' ' + rest.slice(i, i + 74));
    }
  }

  lines.push('END:VCARD');
  return lines.join('\r\n');
}

// ─── Save Contact Handler ─────────────────────────────────────────────────────

function saveContact(c: ContactCard, photoB64?: string, photoMime?: string) {
  const vcard = buildVCard(c, photoB64, photoMime);
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${c.name.replace(/\s+/g, '_')}_HSG.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Share Handler ────────────────────────────────────────────────────────────

async function shareContact(c: ContactCard, photoB64?: string, photoMime?: string) {
  const vcard = buildVCard(c, photoB64, photoMime);
  const blob = new Blob([vcard], { type: 'text/vcard' });
  const file = new File([blob], `${c.name.replace(/\s+/g, '_')}_HSG.vcf`, { type: 'text/vcard' });

  if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ title: `${c.name} — ${c.company}`, files: [file] });
      return;
    } catch { /* cancelled */ }
  }

  if (navigator.share) {
    try {
      await navigator.share({
        title: `${c.name} — ${c.company}`,
        text: `${c.name}\n${c.title}\n${c.company}\n${c.email}\n${c.phone}\n${c.website}`,
        url: window.location.href,
      });
      return;
    } catch { /* cancelled */ }
  }

  saveContact(c);
}

// ─── Tilt Card Wrapper ────────────────────────────────────────────────────────

interface TiltCardProps {
  onClick: () => void;
  disabled?: boolean;
  walletStatus: 'idle' | 'sharing' | 'done';
  children: React.ReactNode;
}

function TiltCard({ onClick, disabled, walletStatus, children }: TiltCardProps) {
  const wrapRef = useRef<HTMLButtonElement>(null);
  const glareRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  // Current tilt values (used for smooth lerp)
  const current = useRef({ rx: 0, ry: 0, glareX: 50, glareY: 50, glareO: 0 });
  const target = useRef({ rx: 0, ry: 0, glareX: 50, glareY: 50, glareO: 0 });
  const isHovering = useRef(false);

  const MAX_TILT = 12; // degrees
  const GLARE_MAX = 0.28;

  const applyTransform = useCallback(() => {
    const el = wrapRef.current;
    const gl = glareRef.current;
    if (!el) return;
    const { rx, ry, glareX, glareY, glareO } = current.current;
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale3d(1.03,1.03,1.03)`;
    if (gl) {
      gl.style.background = `radial-gradient(circle at ${glareX}% ${glareY}%, rgba(201,169,110,${glareO}) 0%, transparent 65%)`;
    }
  }, []);

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  const animate = useCallback(() => {
    const SPEED = isHovering.current ? 0.12 : 0.08;
    const c = current.current;
    const t = target.current;
    c.rx = lerp(c.rx, t.rx, SPEED);
    c.ry = lerp(c.ry, t.ry, SPEED);
    c.glareX = lerp(c.glareX, t.glareX, SPEED);
    c.glareY = lerp(c.glareY, t.glareY, SPEED);
    c.glareO = lerp(c.glareO, t.glareO, SPEED);
    applyTransform();

    const stillMoving =
      Math.abs(c.rx - t.rx) > 0.01 ||
      Math.abs(c.ry - t.ry) > 0.01 ||
      Math.abs(c.glareO - t.glareO) > 0.001;

    if (stillMoving) {
      rafRef.current = requestAnimationFrame(animate);
    } else {
      // Snap to rest
      Object.assign(c, t);
      applyTransform();
      if (!isHovering.current) {
        const el = wrapRef.current;
        if (el) el.style.transform = '';
      }
    }
  }, [applyTransform]);

  const startAnimate = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
  }, [animate]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;   // 0–1
    const y = (e.clientY - rect.top) / rect.height;    // 0–1
    target.current = {
      rx: (0.5 - y) * MAX_TILT * 2,
      ry: (x - 0.5) * MAX_TILT * 2,
      glareX: x * 100,
      glareY: y * 100,
      glareO: GLARE_MAX,
    };
    startAnimate();
  }, [startAnimate]);

  const handleMouseEnter = useCallback(() => {
    isHovering.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHovering.current = false;
    target.current = { rx: 0, ry: 0, glareX: 50, glareY: 50, glareO: 0 };
    startAnimate();
  }, [startAnimate]);

  // Touch support
  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLButtonElement>) => {
    const el = wrapRef.current;
    if (!el || !e.touches[0]) return;
    const rect = el.getBoundingClientRect();
    const x = (e.touches[0].clientX - rect.left) / rect.width;
    const y = (e.touches[0].clientY - rect.top) / rect.height;
    target.current = {
      rx: (0.5 - y) * MAX_TILT,
      ry: (x - 0.5) * MAX_TILT,
      glareX: x * 100,
      glareY: y * 100,
      glareO: GLARE_MAX * 0.6,
    };
    isHovering.current = true;
    startAnimate();
  }, [startAnimate]);

  const handleTouchEnd = useCallback(() => {
    isHovering.current = false;
    target.current = { rx: 0, ry: 0, glareX: 50, glareY: 50, glareO: 0 };
    startAnimate();
  }, [startAnimate]);

  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <button
      ref={wrapRef}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      className="relative group w-full cursor-pointer focus:outline-none disabled:cursor-wait"
      style={{
        WebkitTapHighlightColor: 'transparent',
        transformStyle: 'preserve-3d',
        willChange: 'transform',
        transition: 'box-shadow 0.3s ease',
      }}
      aria-label="Add to Apple Wallet"
    >
      {/* Ambient glow that intensifies on hover */}
      <div
        className="absolute -inset-2 rounded-3xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-400"
        style={{
          background: 'radial-gradient(ellipse at 50% 60%, rgba(201,169,110,0.15) 0%, transparent 70%)',
          filter: 'blur(8px)',
        }}
      />

      {/* Card content */}
      <div className="relative" style={{ transformStyle: 'preserve-3d' }}>
        {children}

        {/* Glare layer — sits on top of card, pointer-events none */}
        <div
          ref={glareRef}
          className="absolute inset-0 rounded-[20px] pointer-events-none"
          style={{ mixBlendMode: 'screen', zIndex: 10 }}
        />
      </div>

      {/* Tap-to-add overlay badge */}
      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-200 group-hover:opacity-100 opacity-0"
        style={{
          background: 'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(201,169,110,0.35)',
          whiteSpace: 'nowrap',
          zIndex: 20,
        }}
      >
        {walletStatus === 'sharing' ? (
          <div className="w-3 h-3 border border-white/30 border-t-white/80 rounded-full animate-spin" />
        ) : (
          <i className="ri-apple-line text-[10px]" style={{ color: '#C9A96E' }}></i>
        )}
        <span className="text-[9px] font-medium tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.85)' }}>
          {walletStatus === 'sharing' ? 'Opening…' : 'Add to Wallet'}
        </span>
      </div>
    </button>
  );
}

// ─── QR Modal ─────────────────────────────────────────────────────────────────

function QRModal({ card, onClose }: { card: ContactCard; onClose: () => void }) {
  const [qrUrl, setQrUrl] = useState('');
  const vcard = buildVCard(card);

  useEffect(() => {
    QRCode.toDataURL(vcard, {
      width: 300,
      margin: 2,
      color: { dark: '#1A1A1A', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    }).then(setQrUrl).catch(() => {});
  }, [vcard]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-xs p-6 flex flex-col items-center gap-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between w-full">
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A]">Scan to Save</p>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F7F4EF] text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
        <div className="bg-white rounded-xl p-3 border border-[#E8E6E3]">
          {qrUrl ? (
            <img src={qrUrl} alt="QR Code" className="w-[220px] h-[220px]" />
          ) : (
            <div className="w-[220px] h-[220px] flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <p className="text-[11px] text-[#9A9A9A] text-center leading-relaxed">
          Point your iPhone camera at this code to instantly save {card.firstName}&apos;s contact.
        </p>
        {qrUrl && (
          <a
            href={qrUrl}
            download={`${card.name.replace(/\s+/g, '_')}_HSG_QR.png`}
            className="flex items-center gap-2 text-xs text-[#8B7355] hover:text-[#C9A96E] transition-colors cursor-pointer"
          >
            <i className="ri-download-line text-sm"></i>
            Download QR image
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Contact Info Row ─────────────────────────────────────────────────────────

function ContactRow({ icon, label, value, href, external }: {
  icon: string; label: string; value: string; href: string; external?: boolean;
}) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="group flex items-center gap-4 py-4 border-b border-white/8 last:border-0 cursor-pointer"
    >
      <div
        className="w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0 transition-all group-hover:scale-105"
        style={{ backgroundColor: 'rgba(201,169,110,0.1)', border: '1px solid rgba(201,169,110,0.25)' }}
      >
        <i className={`${icon} text-base`} style={{ color: '#C9A96E' }}></i>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] mb-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{label}</p>
        <p className="text-sm font-light truncate transition-colors group-hover:text-[#C9A96E]" style={{ color: 'rgba(255,255,255,0.85)' }}>{value}</p>
      </div>
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#C9A96E' }}>
        <i className="ri-arrow-right-s-line text-base"></i>
      </div>
    </a>
  );
}

// ─── Public Card Page ─────────────────────────────────────────────────────────

export default function PublicCardPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const card = CARD_REGISTRY.find((c) => c.slug === slug?.toLowerCase());

  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [photoB64, setPhotoB64] = useState('');
  const [photoMime, setPhotoMime] = useState('');
  const [photoReady, setPhotoReady] = useState(false);
  const [walletStatus, setWalletStatus] = useState<'idle' | 'sharing' | 'done'>('idle');

  useEffect(() => {
    if (!card) return;
    imageUrlToBase64(card.image).then((result) => {
      if (result) { setPhotoB64(result.b64); setPhotoMime(result.mime); }
      setPhotoReady(true);
    });
  }, [card?.image]);

  const cardUrl = card ? `https://higherstandardglobal.com/card/${card.slug}` : 'https://higherstandardglobal.com';

  useSEO({
    title: card
      ? `${card.name} — ${card.title} | Higher Standard Global`
      : 'Digital Business Card | Higher Standard Global',
    description: card
      ? `Connect with ${card.name}, ${card.title} at Higher Standard Global. Save contact details directly to your phone.`
      : 'Higher Standard Global digital business card.',
    canonical: card ? `/card/${card.slug}` : '/card',
    ogTitle: card ? `${card.name} — ${card.title}` : 'Higher Standard Global',
    ogDescription: card
      ? `${card.title} at Higher Standard Global. Tap to save contact.`
      : 'Higher Standard Global digital business card.',
    ogImage: card ? card.image : undefined,
    ogType: 'profile',
  });

  useEffect(() => {
    if (!card) return;
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', `${card.name} — ${card.title}`);
    setMeta('meta[name="twitter:description"]', 'content', `${card.title} at Higher Standard Global. Tap to save contact.`);
    setMeta('meta[name="twitter:image"]', 'content', card.image);
    setMeta('meta[property="og:image:width"]', 'content', '480');
    setMeta('meta[property="og:image:height"]', 'content', '640');
    setMeta('meta[property="og:site_name"]', 'content', 'Higher Standard Global');
    setMeta('meta[property="og:url"]', 'content', cardUrl);
    setMeta('meta[property="profile:first_name"]', 'content', card.firstName);
    setMeta('meta[property="profile:username"]', 'content', card.slug);
  }, [card, cardUrl]);

  if (!card) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex flex-col items-center justify-center gap-4 px-6">
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 28, fontWeight: 600, letterSpacing: '0.15em', color: '#C9A96E' }}>HSG</span>
        <p className="text-white/40 text-sm text-center">This card link is not available.</p>
        <button onClick={() => navigate('/')} className="text-xs text-[#C9A96E] hover:text-white transition-colors cursor-pointer mt-2">
          Visit higherstandardglobal.com
        </button>
      </div>
    );
  }

  const handleSave = async () => {
    if (saving || saved) return;
    setSaving(true);
    await new Promise((r) => setTimeout(r, 300));
    saveContact(card, photoB64 || undefined, photoMime || undefined);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 4000);
  };

  const handleShare = () => shareContact(card, photoB64 || undefined, photoMime || undefined);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
  };

  const iMessageUrl = `sms:&body=Hey! Here's my digital business card — ${card.name}, ${card.title} at ${card.company}. Save my contact in one tap: ${cardUrl}`;

  const handleSendToWallet = async () => {
    if (walletStatus === 'sharing') return;
    setWalletStatus('sharing');
    try {
      const vcard = buildVCard(card, photoB64 || undefined, photoMime || undefined);
      const blob = new Blob([vcard], { type: 'text/vcard' });
      const safeName = card.name.replace(/\s+/g, '_');
      const file = new File([blob], `${safeName}_HSG.vcf`, { type: 'text/vcard' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${card.name} — ${card.company}`,
          text: `${card.name}, ${card.title} at ${card.company}`,
          files: [file],
        });
      } else if (navigator.share) {
        await navigator.share({
          title: `${card.name} — ${card.company}`,
          text: `${card.name}, ${card.title} at ${card.company}\n${card.email}\n${card.phone}\n${cardUrl}`,
          url: cardUrl,
        });
      } else {
        // Fallback: download vcf
        saveContact(card, photoB64 || undefined, photoMime || undefined);
      }
      setWalletStatus('done');
      setTimeout(() => setWalletStatus('idle'), 3000);
    } catch {
      setWalletStatus('idle');
    }
  };

  return (
    <>
      {showQR && <QRModal card={card} onClose={() => setShowQR(false)} />}
      {showExport && (
        <DigitalCardExport
          card={{
            name: card.name,
            title: card.title,
            email: card.email,
            phone: card.phone,
            website: card.website,
            tagline: card.tagline,
          }}
          onClose={() => setShowExport(false)}
        />
      )}

      <div
        className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(160deg, #111111 0%, #1E1E1E 50%, #141414 100%)' }}
      >
        {/* ── Top bar ── */}
        <div className="w-full flex items-center justify-between px-6 pt-6 pb-0">
          <div className="flex items-center gap-2">
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 18, fontWeight: 600, letterSpacing: '0.15em', color: '#C9A96E', lineHeight: 1 }}>HSG</span>
            <div style={{ width: 1, height: 16, backgroundColor: 'rgba(201,169,110,0.3)' }} />
            <div className="flex flex-col gap-0.5">
              <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 6.5, fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#C9A96E', lineHeight: 1 }}>Higher Standard</span>
              <span style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 6.5, fontWeight: 300, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', lineHeight: 1 }}>Global</span>
            </div>
          </div>
          <a
            href="https://higherstandardglobal.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] uppercase tracking-[0.2em] text-white/30 hover:text-[#C9A96E] transition-colors cursor-pointer"
          >
            Visit Website
          </a>
        </div>

        {/* ── Hero ── */}
        <div className="relative w-full flex-shrink-0" style={{ height: '48vh', minHeight: 280, maxHeight: 420 }}>
          <img
            src={card.image}
            alt=""
            className="w-full h-full object-cover object-top"
            style={{ opacity: imgLoaded ? 0.45 : 0, transition: 'opacity 0.8s ease', mixBlendMode: 'luminosity' }}
            onLoad={() => setImgLoaded(true)}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(17,17,17,0.2) 0%, transparent 35%, rgba(17,17,17,0.98) 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(17,17,17,0.7) 0%, transparent 65%)' }} />

          {/* Name + title */}
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-7">
            <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 40, fontWeight: 500, color: '#FFFFFF', letterSpacing: '0.02em', lineHeight: 1.05, margin: 0 }}>
              {card.name}
            </p>
            <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 10, fontWeight: 400, color: '#C9A96E', letterSpacing: '0.2em', textTransform: 'uppercase', margin: 0, marginTop: 7 }}>
              {card.title}
            </p>
            <p style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 10, fontWeight: 300, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', margin: 0, marginTop: 3 }}>
              {card.company}
            </p>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="flex-1 flex flex-col max-w-sm mx-auto w-full px-5 pt-5 pb-12 gap-0">

          {/* ── PRIMARY CTA: Save to Contacts ── */}
          <div className="mb-6">
            <button
              onClick={handleSave}
              disabled={!photoReady || saving}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-sm font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap relative overflow-hidden"
              style={{
                background: saved
                  ? 'linear-gradient(135deg, #3D6B4A, #4A7C59)'
                  : 'linear-gradient(135deg, #C9A96E 0%, #D4B87A 50%, #B8935A 100%)',
                color: saved ? '#FFFFFF' : '#1A1A1A',
                boxShadow: saved ? '0 0 0 1px rgba(74,124,89,0.4)' : '0 0 0 1px rgba(201,169,110,0.3), 0 8px 32px rgba(201,169,110,0.2)',
              }}
            >
              {/* Shimmer */}
              {!saved && !saving && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)',
                    animation: 'shimmer 2.5s infinite',
                  }}
                />
              )}
              <style>{`@keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }`}</style>

              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#1A1A1A]/30 border-t-[#1A1A1A] rounded-full animate-spin" />
                  <span>Preparing contact…</span>
                </>
              ) : saved ? (
                <>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-lg"></i>
                  </div>
                  <span>Contact Saved!</span>
                </>
              ) : (
                <>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-add-line text-lg"></i>
                  </div>
                  <span>Save to Contacts</span>
                  <span className="text-[10px] font-normal opacity-60 ml-1">· .vcf</span>
                </>
              )}
            </button>

            {/* Sub-hint */}
            <p className="text-center text-[10px] mt-2.5" style={{ color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em' }}>
              {photoReady ? 'Downloads a vCard file — works on iPhone, Android & Outlook' : 'Loading contact photo…'}
            </p>
          </div>

          {/* ── SECONDARY CTA: Add to Apple Wallet ── */}
          <div className="mb-6">
            <button
              onClick={handleSendToWallet}
              disabled={walletStatus === 'sharing'}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl text-sm font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap relative overflow-hidden disabled:opacity-70"
              style={{
                background: walletStatus === 'done'
                  ? 'rgba(74,124,89,0.15)'
                  : 'rgba(255,255,255,0.05)',
                border: walletStatus === 'done'
                  ? '1px solid rgba(74,124,89,0.35)'
                  : '1px solid rgba(255,255,255,0.12)',
                color: walletStatus === 'done' ? '#4A7C59' : 'rgba(255,255,255,0.85)',
              }}
            >
              {walletStatus === 'sharing' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
                  <span>Opening Share Sheet…</span>
                </>
              ) : walletStatus === 'done' ? (
                <>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-base"></i>
                  </div>
                  <span>Shared!</span>
                </>
              ) : (
                <>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-apple-line text-base" style={{ color: 'rgba(255,255,255,0.7)' }}></i>
                  </div>
                  <span>Add to Apple Wallet</span>
                  <div
                    className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: 'rgba(201,169,110,0.12)', border: '1px solid rgba(201,169,110,0.2)' }}
                  >
                    <i className="ri-share-line text-[9px]" style={{ color: '#C9A96E' }}></i>
                    <span className="text-[9px] font-normal" style={{ color: '#C9A96E', letterSpacing: '0.08em' }}>iOS Share</span>
                  </div>
                </>
              )}
            </button>
            <p className="text-center text-[10px] mt-2" style={{ color: 'rgba(255,255,255,0.2)', letterSpacing: '0.06em' }}>
              Opens the native iOS share sheet — tap &ldquo;Add to Wallet&rdquo; if shown
            </p>
          </div>

          {/* ── Contact info ── */}
          <div
            className="rounded-2xl mb-5"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="px-4 pt-4 pb-1">
              <p className="text-[9px] uppercase tracking-[0.3em]" style={{ color: 'rgba(201,169,110,0.5)' }}>Contact</p>
            </div>
            <div className="px-4 pb-2">
              <ContactRow icon="ri-mail-line" label="Email" value={card.email} href={`mailto:${card.email}`} />
              <ContactRow icon="ri-phone-line" label="Phone" value={card.phone} href={`tel:${card.phone.replace(/\s/g, '')}`} />
              <ContactRow icon="ri-global-line" label="Website" value={card.website} href={`https://${card.website.replace(/^https?:\/\//, '')}`} external />
            </div>
          </div>

          {/* ── Quick actions ── */}
          <div className="grid grid-cols-2 gap-2.5 mb-5">
            {/* iMessage */}
            <a
              href={iMessageUrl}
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl cursor-pointer transition-all"
              style={{ backgroundColor: 'rgba(52,199,89,0.1)', border: '1px solid rgba(52,199,89,0.2)' }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(52,199,89,0.15)' }}>
                <i className="ri-message-2-line text-sm" style={{ color: '#34C759' }}></i>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: '#34C759' }}>iMessage</p>
                <p className="text-[9px]" style={{ color: 'rgba(52,199,89,0.6)' }}>Send card link</p>
              </div>
            </a>

            {/* Share */}
            <button
              onClick={handleShare}
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl cursor-pointer transition-all w-full text-left"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <i className="ri-share-line text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}></i>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Share</p>
                <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>AirDrop, more</p>
              </div>
            </button>

            {/* QR Code */}
            <button
              onClick={() => setShowQR(true)}
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl cursor-pointer transition-all w-full text-left"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <i className="ri-qr-code-line text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}></i>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>QR Code</p>
                <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>Scan to save</p>
              </div>
            </button>

            {/* Copy link */}
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl cursor-pointer transition-all w-full text-left"
              style={{
                backgroundColor: copied ? 'rgba(74,124,89,0.1)' : 'rgba(255,255,255,0.05)',
                border: copied ? '1px solid rgba(74,124,89,0.3)' : '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: copied ? 'rgba(74,124,89,0.15)' : 'rgba(255,255,255,0.08)' }}>
                <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-sm`} style={{ color: copied ? '#4A7C59' : 'rgba(255,255,255,0.7)' }}></i>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: copied ? '#4A7C59' : 'rgba(255,255,255,0.8)' }}>{copied ? 'Copied!' : 'Copy Link'}</p>
                <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>Share this page</p>
              </div>
            </button>
          </div>

          {/* ── Export / All Share Options ── */}
          <div className="mb-5">
            <button
              onClick={() => setShowExport(true)}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl text-sm font-medium tracking-wide transition-all cursor-pointer whitespace-nowrap relative overflow-hidden"
              style={{
                background: 'rgba(201,169,110,0.07)',
                border: '1px solid rgba(201,169,110,0.22)',
                color: '#C9A96E',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-share-box-line text-base"></i>
              </div>
              <span>All Export &amp; Share Options</span>
              <div
                className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'rgba(201,169,110,0.1)', border: '1px solid rgba(201,169,110,0.2)' }}
              >
                <span className="text-[9px] font-normal" style={{ color: 'rgba(201,169,110,0.7)', letterSpacing: '0.08em' }}>7 options</span>
              </div>
            </button>
            <p className="text-center text-[10px] mt-2" style={{ color: 'rgba(255,255,255,0.18)', letterSpacing: '0.06em' }}>
              PNG download · NFC tap · vCard · QR · Wallet · Link
            </p>
          </div>

          {/* ── How to save guide ── */}
          <div
            className="rounded-2xl p-4 mb-5"
            style={{ backgroundColor: 'rgba(201,169,110,0.05)', border: '1px solid rgba(201,169,110,0.12)' }}
          >
            <p className="text-[9px] uppercase tracking-[0.3em] mb-3" style={{ color: 'rgba(201,169,110,0.5)' }}>How to save</p>
            <div className="space-y-2.5">
              {[
                { icon: 'ri-user-add-line', step: 'Tap "Save to Contacts" above', detail: 'Downloads a .vcf file instantly' },
                { icon: 'ri-smartphone-line', step: 'Open the downloaded file', detail: 'iPhone: tap the .vcf in Files or Safari downloads' },
                { icon: 'ri-check-double-line', step: 'Tap "Add to Contacts"', detail: 'Name, phone, email & photo all pre-filled' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(201,169,110,0.12)', border: '1px solid rgba(201,169,110,0.2)' }}>
                    <i className={`${item.icon} text-[10px]`} style={{ color: '#C9A96E' }}></i>
                  </div>
                  <div>
                    <p className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>{item.step}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Apple Wallet Card ── */}
          <div className="flex flex-col gap-3 mb-1">
            <div className="flex items-center gap-2">
              <div style={{ height: 1, flex: 1, background: 'rgba(201,169,110,0.12)' }} />
              <p className="text-[9px] uppercase tracking-[0.3em] flex-shrink-0" style={{ color: 'rgba(201,169,110,0.4)' }}>Digital Card</p>
              <div style={{ height: 1, flex: 1, background: 'rgba(201,169,110,0.12)' }} />
            </div>

            {/* Tappable card wrapper with 3D tilt */}
            <TiltCard
              onClick={handleSendToWallet}
              disabled={walletStatus === 'sharing'}
              walletStatus={walletStatus}
            >
              <AppleWalletPassVisual card={card} />
            </TiltCard>

            <p className="text-center text-[9px]" style={{ color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>
              {walletStatus === 'done'
                ? 'Shared successfully!'
                : 'Tap the card to add it to Apple Wallet'}
            </p>
          </div>

          {/* ── Tagline ── */}
          <p
            className="text-center mt-2"
            style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: 9, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(201,169,110,0.3)' }}
          >
            {card.tagline}
          </p>
        </div>

        {/* ── Footer strip ── */}
        <div
          className="w-full flex flex-col items-center gap-2 py-5 px-6"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <a
            href="https://higherstandardglobal.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 group cursor-pointer"
          >
            <span
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.18em',
                color: 'rgba(201,169,110,0.55)',
                lineHeight: 1,
                transition: 'color 0.2s',
              }}
              className="group-hover:text-[#C9A96E]"
            >
              HSG
            </span>
            <div style={{ width: 1, height: 12, backgroundColor: 'rgba(201,169,110,0.2)' }} />
            <span
              style={{
                fontFamily: 'Inter, Arial, sans-serif',
                fontSize: 9,
                fontWeight: 300,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.2)',
                transition: 'color 0.2s',
              }}
              className="group-hover:text-white/40"
            >
              Higher Standard Global
            </span>
            <i
              className="ri-external-link-line"
              style={{ fontSize: 9, color: 'rgba(201,169,110,0.3)', transition: 'color 0.2s' }}
            ></i>
          </a>
          <p
            style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: 8,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.1)',
            }}
          >
            Powered by Higher Standard Global
          </p>
        </div>
      </div>
    </>
  );
}
